import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Heart, Gift, Loader2, Receipt, LogOut, Crown, Tag, Settings2, Info,
  ShieldCheck, Download, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { paymentsApi, authApi } from "../lib/api";
import { useUsdRate, fmtPrice } from "../lib/price";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const LABELS = {
  premium_monthly: "Premium · monthly",
  premium_annual: "Premium · 1 year",
  calls_50: "AI pack · 50 analyses + 50 searches",
  calls_120: "AI pack · 120 analyses + 120 searches",
  unlock_lifetime: "Premium unlock (legacy)",
  donate_5: "Donation €5",
  donate_10: "Donation €10",
  donate_20: "Donation €20",
};

const Buy = ({ label, sub, onClick, busy, testid, accent = "#2d2824" }) => (
  <button
    onClick={onClick}
    disabled={busy}
    className="flex w-full items-center justify-between rounded-2xl border-2 px-4 py-3 text-left font-bold transition-transform hover:scale-[1.01] active:scale-95 disabled:opacity-60"
    style={{ borderColor: `${accent}30`, background: "#fff", color: accent }}
    data-testid={testid}
  >
    <span>
      {label}
      {sub && <span className="ml-2 text-xs font-semibold text-[#a89e92]">{sub}</span>}
    </span>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>→</span>}
  </button>
);

export const BillingDialog = ({ open, onOpenChange, user, refresh, onLogout, onPricing }) => {
  const [busy, setBusy] = useState("");
  const [giftEmail, setGiftEmail] = useState("");
  const [giftPlan, setGiftPlan] = useState("monthly");
  const [giftCalls, setGiftCalls] = useState(10);
  const [history, setHistory] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [usd, setUsd] = useState(false);
  const rate = useUsdRate();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && user) {
      paymentsApi.history().then((r) => setHistory(r.history || [])).catch(() => {});
      refresh?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buy = async (key) => {
    setBusy(key);
    try {
      const { checkout_url } = await paymentsApi.checkout(key);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setBusy("");
    }
  };

  const manage = async () => {
    setBusy("portal");
    try {
      const { portal_url } = await paymentsApi.portal();
      window.location.href = portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No subscription to manage yet");
      setBusy("");
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Stop the automatic renewal? You keep Premium until the end of the period."))
      return;
    setBusy("cancel");
    try {
      const r = await paymentsApi.cancel();
      toast.success(`Renewal stopped · access until ${new Date(r.access_until).toLocaleDateString()}`);
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy("");
    }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const data = await authApi.exportData();
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "foinn-my-data.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export your data");
    } finally {
      setBusy("");
    }
  };

  const deleteAccount = async () => {
    setBusy("delete");
    try {
      await authApi.deleteAccount();
      toast.success("Your account and data have been deleted");
      setConfirmDelete(false);
      onLogout?.();
    } catch {
      toast.error("Could not delete the account");
    } finally {
      setBusy("");
    }
  };

  const gift = async () => {
    if (!giftEmail.trim()) return;
    setBusy("gift");
    try {
      const res = await paymentsApi.gift(giftEmail.trim(), giftPlan, Number(giftCalls) || 0);
      toast.success(`Gifted to ${res.gifted.email}`);
      setGiftEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not gift");
    } finally {
      setBusy("");
    }
  };

  const isPremium = user && user.plan !== "free";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-md overflow-auto bg-[#fdfbf7]" data-testid="billing-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Your account</DialogTitle>
          <DialogDescription>{user?.email}</DialogDescription>
          <button
            onClick={() => setUsd((v) => !v)}
            className="absolute right-12 top-5 rounded-full border border-[#e5e0d8] bg-white px-2.5 py-1 text-xs font-extrabold text-[#2d2824] hover:bg-[#f3efea]"
            data-testid="currency-toggle"
            title="Show prices in dollars / euro"
          >
            {usd ? "$ USD" : "€ EUR"}
          </button>
        </DialogHeader>
        {usd && (
          <p className="text-[11px] font-semibold text-[#a89e92]">
            Dollar amounts are indicative — Stripe charges in euro.
          </p>
        )}

        {/* plan + analyses */}
        <div className="rounded-2xl bg-[#2d2824] p-4 text-white">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-white/70">
            {isPremium ? <Crown className="h-4 w-4 text-[#ffd479]" /> : <Sparkles className="h-4 w-4" />}
            {user?.plan_label || "Signed in"}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-black" data-testid="calls-balance">
              {user?.calls_left ?? 0}
            </span>
            <span className="text-sm font-semibold text-white/70">AI analyses available</span>
          </div>
          <div className="mt-1 text-xs font-semibold text-white/70">
            {user?.unlimited_searches
              ? "Unlimited searches"
              : `20 searches a day${user?.search_extra ? ` + ${user.search_extra} extra` : ""} · Premium removes the limit`}
            {user?.plan_expires_at && (
              <> · renews {new Date(user.plan_expires_at).toLocaleDateString()}</>
            )}
          </div>
        </div>

        {!isPremium ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#8a2be2]">
              <Crown className="h-4 w-4" /> Go Premium
            </div>
            <Buy
              label={`${fmtPrice(2, usd, rate)} / month`}
              sub="unlimited searches · 10 analyses/month"
              onClick={() => buy("premium_monthly")}
              busy={busy === "premium_monthly"}
              testid="buy-monthly"
              accent="#8a2be2"
            />
            <Buy
              label={`${fmtPrice(20, usd, rate)} / year`}
              sub="unlimited searches · 15 analyses/month"
              onClick={() => buy("premium_annual")}
              busy={busy === "premium_annual"}
              testid="buy-annual"
              accent="#0f8a5f"
            />
            <button
              onClick={onPricing}
              className="flex w-full items-center justify-center gap-1.5 text-xs font-bold text-[#6e655b] hover:underline"
              data-testid="billing-compare"
            >
              <Tag className="h-3.5 w-3.5" /> Compare all plans
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={manage}
              disabled={busy === "portal"}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#e5e0d8] bg-white px-4 py-3 text-sm font-bold text-[#2d2824] hover:bg-[#f3efea] disabled:opacity-60"
              data-testid="manage-subscription"
            >
              <Settings2 className="h-4 w-4" /> Manage subscription & invoices
            </button>
            {user?.subscription_status === "cancelling" ? (
              <p className="text-center text-xs font-bold text-[#8a4300]" data-testid="cancel-note">
                Renewal stopped · access until{" "}
                {user.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : "—"}
              </p>
            ) : (
              user?.plan !== "founder" && (
                <button
                  onClick={cancelSub}
                  disabled={busy === "cancel"}
                  className="w-full text-center text-xs font-bold text-[#6e655b] hover:text-[#e11d48] hover:underline"
                  data-testid="cancel-subscription"
                >
                  Cancel renewal (keeps access until the period ends)
                </button>
              )
            )}
            <button
              onClick={onPricing}
              className="flex w-full items-center justify-center gap-1.5 text-xs font-bold text-[#6e655b] hover:underline"
              data-testid="billing-compare-premium"
            >
              <Tag className="h-3.5 w-3.5" /> Compare all plans
            </button>
          </div>
        )}

        <div>
          <div className="mb-1 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#2d2824]">
            <Sparkles className="h-4 w-4" /> Extra analyses & searches
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Buy label={fmtPrice(5, usd, rate)} sub="50 analyses + 50 searches" onClick={() => buy("calls_50")} busy={busy === "calls_50"} testid="buy-calls-50" />
            <Buy label={fmtPrice(10, usd, rate)} sub="120 analyses + 120 searches" onClick={() => buy("calls_120")} busy={busy === "calls_120"} testid="buy-calls-120" />
          </div>
        </div>

        <div>
          <div className="mb-1 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#e11d48]">
            <Heart className="h-4 w-4" /> Support Foinn.org
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-[#e11d48]/40 text-[#e11d48]"
                    data-testid="donate-info"
                  >
                    <Info className="h-3 w-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] text-xs">
                  Donations go straight into Foinn.org’s future development — hosting, the tune
                  database, AI services and new features. Nothing else.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Buy label={fmtPrice(5, usd, rate)} onClick={() => buy("donate_5")} busy={busy === "donate_5"} testid="donate-5" accent="#e11d48" />
            <Buy label={fmtPrice(10, usd, rate)} onClick={() => buy("donate_10")} busy={busy === "donate_10"} testid="donate-10" accent="#e11d48" />
            <Buy label={fmtPrice(20, usd, rate)} onClick={() => buy("donate_20")} busy={busy === "donate_20"} testid="donate-20" accent="#e11d48" />
          </div>
        </div>

        {history.length > 0 && (
          <div>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
              <Receipt className="h-4 w-4" /> Purchase history
            </div>
            <div className="max-h-32 overflow-auto rounded-2xl border border-[#e5e0d8] bg-white" data-testid="purchase-history">
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between border-b border-[#f3efea] px-3 py-2 text-sm last:border-0">
                  <span className="font-semibold text-[#2d2824]">{LABELS[h.lookup_key] || h.lookup_key}</span>
                  <span className="flex items-center gap-2 text-[#6e655b]">
                    {h.created_at && <span className="text-xs">{new Date(h.created_at).toLocaleDateString()}</span>}
                    <span className="font-bold text-[#2d2824]">€{Number(h.amount).toFixed(2)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {user?.is_admin && (
          <div className="rounded-2xl border border-dashed border-[#e5e0d8] p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
              <Gift className="h-4 w-4" /> Admin · gift plan
            </div>
            <input
              value={giftEmail}
              onChange={(e) => setGiftEmail(e.target.value)}
              placeholder="user@email.com"
              className="mb-2 w-full rounded-xl border border-[#e5e0d8] bg-white px-3 py-2 text-sm"
              data-testid="gift-email"
            />
            <div className="flex items-center gap-2">
              <select
                value={giftPlan}
                onChange={(e) => setGiftPlan(e.target.value)}
                className="rounded-xl border border-[#e5e0d8] bg-white px-2 py-2 text-sm"
                data-testid="gift-plan"
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="none">Calls only</option>
              </select>
              <input
                type="number"
                value={giftCalls}
                onChange={(e) => setGiftCalls(e.target.value)}
                className="w-20 rounded-xl border border-[#e5e0d8] bg-white px-3 py-2 text-sm"
                data-testid="gift-calls"
              />
              <button
                onClick={gift}
                disabled={busy === "gift"}
                className="flex-1 rounded-xl bg-[#2d2824] px-3 py-2 text-sm font-bold text-white disabled:opacity-60"
                data-testid="gift-submit"
              >
                {busy === "gift" ? "Gifting…" : "Gift"}
              </button>
            </div>
          </div>
        )}

        {onLogout && (
          <button
            onClick={onLogout}
            className="mt-1 flex w-full items-center justify-center gap-2 rounded-full border border-[#e5e0d8] bg-white px-4 py-2.5 text-sm font-bold text-[#6e655b] transition-colors hover:bg-[#f3efea] hover:text-[#e11d48]"
            data-testid="signout-button"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        )}

        {/* GDPR — your data is yours */}
        <div className="rounded-2xl border border-[#e5e0d8] bg-white p-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
            <ShieldCheck className="h-4 w-4" /> Your data
          </div>
          <p className="mb-2 text-xs text-[#6e655b]">
            Foinn.org stores your Google name and email, your favourites and your purchase records.
            You can take a copy or erase everything at any time.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportData}
              disabled={busy === "export"}
              className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] px-3 py-2 text-xs font-bold text-[#2d2824] hover:bg-[#f3efea] disabled:opacity-60"
              data-testid="export-data"
            >
              <Download className="h-3.5 w-3.5" /> Download my data
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-1.5 rounded-full border border-[#e11d48]/40 px-3 py-2 text-xs font-bold text-[#e11d48] hover:bg-[#fff1f3]"
              data-testid="delete-account"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete my account
            </button>
            <button
              onClick={() => {
                onOpenChange(false);
                navigate("/privacy");
              }}
              className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] px-3 py-2 text-xs font-bold text-[#2d2824] hover:bg-[#f3efea]"
              data-testid="privacy-link"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Privacy & cancelling
            </button>
          </div>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent className="bg-[#fdfbf7]" data-testid="delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-2xl">
                Delete everything, for good?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This is <b>permanent and cannot be undone</b>. Your account, favourites, search
                history and remaining AI analyses will be erased, and any active subscription will
                be cancelled immediately. Payment records are kept anonymously for accounting only.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel">Keep my account</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteAccount}
                disabled={busy === "delete"}
                className="bg-[#e11d48] hover:bg-[#be123c]"
                data-testid="delete-confirm-button"
              >
                {busy === "delete" ? "Deleting…" : "Delete permanently"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
